import LottieAnimation from "common/Animations/LottieAnimation.tsx";
import Modal from "common/Modal/Modal";
import React, { useEffect, useState } from "react";
import signalAnimation from "features/bots/BotsIntroduction/signal.json";
import darkSignalAnimation from "features/bots/BotsIntroduction/dark_signal.json";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import { typography } from "@shared/common/Themes/typography.ts";
import Flex from "common/Boxes/Flex";
import { sharedLib } from "@shared/services/shared.service.ts";
import useQuery from "utils/hooks/useQuery.ts";
import { matchPath, useLocation } from "react-router-dom";
import { SIGNALS_BOT_PATH } from "common/constants.ts";
import {
  ClockIcon,
  DarkClockIcon,
  DarkSignalIcon,
  SignalIcon,
} from "common/Icons/Icons.tsx";
import Link from "common/Typography/Link.tsx";
import Button from "common/Buttons/Button";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery.ts";
import { formatDate, getFormattedTimeDuration } from "@shared/utils/date.ts";
import { skipToken } from "@reduxjs/toolkit/query";
import LineSkeleton from "common/Skeleton/LineSkeleton.tsx";
import { NEW_BOT_ID_QUERY_PARAM } from "features/bots/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import ArtStage from "features/onboarding/art/ArtStage";
import { SignalsThumb } from "features/onboarding/art/MethodThumbs";

const SignalsBotIntroductionModal = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const theme = useTheme();
  const location = useLocation();
  const { appendQuery } = useHistoryWithQuery();
  const query = useQuery();
  const newSignalsBotQuery = query.get(NEW_BOT_ID_QUERY_PARAM);
  const signalsBotId = Number(newSignalsBotQuery);
  const match = !!matchPath(location.pathname, {
    path: SIGNALS_BOT_PATH,
  });
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (match && !!newSignalsBotQuery) {
      setOpen(true);
    }
  }, [match, newSignalsBotQuery]);

  const { data, isLoading } =
    sharedLib.sharedApi.bots.useGetSubGroupSignalFrequencyQuery(
      signalsBotId
        ? {
            bot_id: signalsBotId,
          }
        : skipToken
    );

  const closeModal = () => {
    setOpen(false);
    appendQuery(undefined, [NEW_BOT_ID_QUERY_PARAM]);
  };

  const getItem = (
    Icon: (props: any) => React.JSX.Element,
    title: string,
    value: string
  ) => {
    if (theme.isNewLook)
      return (
        <NewLookItem>
          <ItemLabel>{title}</ItemLabel>
          {isLoading ? (
            <LineSkeleton style={{ width: 75 }} />
          ) : (
            <ItemValue>{value}</ItemValue>
          )}
        </NewLookItem>
      );
    return (
      <Flex $justifyContent="flex-start">
        <Icon style={{ width: 55, height: 55, marginRight: 12 }} />
        <div>
          <Text type="medium" style={{ marginBottom: 4 }}>
            {title}
          </Text>
          {isLoading ? (
            <LineSkeleton style={{ width: 75 }} />
          ) : (
            <Text $weight={typography.fontWeights.SEMI_BOLD}>{value}</Text>
          )}
        </div>
      </Flex>
    );
  };

  const extraInfo = (
    <ItemsWrapper $isBottomNavigator={isBottomNavigator}>
      {getItem(
        theme.isDarkMode ? DarkSignalIcon : SignalIcon,
        t("modals.signal_frequency"),
        !data?.signals_frequency
          ? t("common.na")
          : `~ Every ${getFormattedTimeDuration(data?.signals_frequency)}`
      )}
      {getItem(
        theme.isDarkMode ? DarkClockIcon : ClockIcon,
        t("modals.last_signal"),
        !data?.last_signal_open_time
          ? t("common.na")
          : `${formatDate(data?.last_signal_open_time, true, true)}`
      )}
    </ItemsWrapper>
  );

  return (
    <Modal
      open={open}
      onCancel={closeModal}
      width={520}
      styles={{ body: { padding: 0 } }}
    >
      <Wrapper>
        {theme.isNewLook ? (
          <ArtHeader $isBottomNavigator={isBottomNavigator}>
            <ArtStage height={200} phoneHeight={170}>
              <SignalsThumb />
            </ArtStage>
          </ArtHeader>
        ) : (
          <LottieAnimation
            animationData={
              theme.isDarkMode ? darkSignalAnimation : signalAnimation
            }
          />
        )}
        <ContentWrapper $isBottomNavigator={isBottomNavigator}>
          <ModalTitle>{t("modals.signals_bot_introduction_title")}</ModalTitle>
          <Text>{t("modals.signals_bot_introduction_content")}</Text>
          {extraInfo}
          <LinkWrapper>
            {theme.isNewLook ? (
              <Button onClick={closeModal}>{t("buttons.close")}</Button>
            ) : (
              <Link onClick={closeModal}>Close</Link>
            )}
          </LinkWrapper>
        </ContentWrapper>
      </Wrapper>
    </Modal>
  );
};

export default React.memo(SignalsBotIntroductionModal);

const Wrapper = styled.div`
  svg {
    border-radius: 6px;
  }
`;
const ContentWrapper = styled.div<{ $isBottomNavigator: boolean }>`
  padding: ${(p) => (p.$isBottomNavigator ? 20 : 36)}px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    padding: ${p.$isBottomNavigator ? "20px" : "24px 32px 28px"};
  `}
`;
const ArtHeader = styled.div<{ $isBottomNavigator: boolean }>`
  padding: ${(p) => (p.$isBottomNavigator ? "20px 20px 0" : "32px 32px 0")};
`;
// New look: label over figure, side by side in one hairline strip (no icon
// tiles).
const NewLookItem = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  & + & {
    box-shadow: -1px 0 0 ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const ItemLabel = styled.div`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
`;
const ItemValue = styled.div`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const ModalTitle = styled(Title).attrs({ level: 3 })`
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  margin-bottom: 12px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    &.ant-typography {
      margin-bottom: 8px;
      font-size: 18px;
      line-height: 26px;
      font-weight: 600;
    }
  `}
`;
const LinkWrapper = styled.div`
  text-align: right;
  margin-top: 32px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    margin-top: 28px;
  `}
`;
const ItemsWrapper = styled(Flex)<{ $isBottomNavigator?: boolean }>`
  justify-content: flex-start;
  align-items: ${(p) => (p.$isBottomNavigator ? "flex-start" : "center")};
  flex-direction: ${(p) => (p.$isBottomNavigator ? "column" : "row")};
  column-gap: 60px;
  row-gap: 16px;
  margin-top: 32px;
  ${(p) =>
    p.theme.isNewLook &&
    `
    flex-direction: row;
    align-items: stretch;
    gap: 0;
    margin-top: 20px;
    border-radius: ${NEW_LOOK_RADIUS.control}px;
    box-shadow: inset 0 0 0 1px ${getNewLookTokens(p.theme).hairline};
  `}
`;
